import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";

import leftArrow from "../../assets/icons/left-arrow.svg";
import logoCobee from "../../assets/logo-cobee.svg";
import Button from "../../components/button";
import { Footer } from "../../components/footer";
import { useMetrics } from "../../metrics";
import { ROUTES } from "../../routes";
import classes from "./AddEmail.module.css";
import { useVerificationEmail } from "./useVerificationEmail";

const BUTTON_DISABLED_TIMEOUT = 60000;

const AddEmail = () => {
  const [showEmail, setShowEmail] = useState(true);
  const [disableResendEmail, setDisableResendEmail] = useState(true);
  const [backButtonVisible, setBackButtonVisible] = useState(true);
  const { postVerificationEmail } = useVerificationEmail();
  const timer = useRef(null);
  const [email, setEmail] = useState("");
  const [timeLeft, setTimeLeft] = useState(BUTTON_DISABLED_TIMEOUT);
  const [intervalIdLocal, setIntervalIdLocal] = useState();
  const { pageView, trackAction } = useMetrics();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (showEmail) {
      pageView("AddEmail");
      return;
    }
    pageView("VerifyEmail");
  }, [showEmail, pageView]);

  const handlerTimer = () => {
    setDisableResendEmail(true);
    const expirationTime = Date.now() + BUTTON_DISABLED_TIMEOUT;
    const intervalId = setInterval(() => {
      const timeLeft = expirationTime - Date.now();
      if (timeLeft <= 0) {
        clearInterval(intervalId);
        setDisableResendEmail(false);
        setTimeLeft(0);
        return;
      }
      setTimeLeft(timeLeft);
    }, 1000);
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    setIntervalIdLocal(intervalId);
  };

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const handleOnSubmit = async (event) => {
    event.preventDefault();
    const emailValue = event.target.elements.email.value;
    setEmail(emailValue);

    await postVerificationEmail(emailValue);
    setShowEmail(false);
    handlerTimer();
  };

  const handleResendEmailOnPress = async () => {
    setTimeLeft(BUTTON_DISABLED_TIMEOUT);
    await postVerificationEmail(email);
    trackAction.click({
      screenName: "VerifyEmail",
      componentName: "button resendEmail",
    });
    handlerTimer();
  };

  const handleGoBackButton = () => {
    setShowEmail(true);
    clearInterval(intervalIdLocal);
    setTimeLeft(BUTTON_DISABLED_TIMEOUT);
  };

  const handleOnClickContinueButton = () => {
    trackAction.click({
      screenName: "AddEmail",
      componentName: "button continue",
    });
  };

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => clearTimeout(timer.current);
  }, []);

  useEffect(() => {
    const email = searchParams.get("email");
    if (email) {
      setBackButtonVisible(false);
      setEmail(email);
      postVerificationEmail(email);
      setShowEmail(false);
      handlerTimer();
    }
  }, []);

  return (
    <div className={classes.container}>
      {showEmail ? (
        <>
          <header className="grid grid-cols-12 mt-4 h-16 items-center">
            <Link
              className={`col-span-2 justify-end ${classes.goBack}`}
              to={ROUTES.HOME}
            >
              <img src={leftArrow} alt="" />
              {t("common.back")}
            </Link>
            <div className="flex justify-center col-start-3 col-span-8">
              <img
                className={`hidden md:flex  ${classes.logo}`}
                src={logoCobee}
                alt="Logo de Cobee"
              />
            </div>
          </header>
          <form className={classes.infoContainer} onSubmit={handleOnSubmit}>
            <h1 className={classes.title}>{t("signUpFlow.addEmail.title")}</h1>
            <p className={classes.description}>
              {t("signUpFlow.addEmail.description")}
            </p>
            <div className={classes.inputEmailContainer}>
              <input
                className={classes.inputEmail}
                required
                placeholder={t("signUpFlow.addEmail.placeholder")}
                type="email"
                id="email"
              />
            </div>
            <div className={classes.buttonContainer}>
              <Button
                type="submit"
                onClick={handleOnClickContinueButton}
                text={t("continue.button")}
              />
            </div>
          </form>
        </>
      ) : (
        <>
          <header className="grid grid-cols-12 mt-4 h-16 items-center">
            {backButtonVisible ? (
              <button
                className={`col-span-2 justify-end ${classes.goBack}`}
                onClick={handleGoBackButton}
              >
                <img src={leftArrow} alt="" />
                {t("common.back")}
              </button>
            ) : null}
            <div className="flex justify-center col-start-3 col-span-8">
              <img
                className={classes.logo}
                src={logoCobee}
                alt={t("common.logo")}
              />
            </div>
          </header>
          <div>
            <div className={classes.infoContainer}>
              <h1 className={classes.title}>
                {t("signUpFlow.verifyEmail.title")}
              </h1>
              <p className={classes.description}>
                {t("signUpFlow.verifyEmail.description", { email })}
              </p>
              <button
                onClick={handleResendEmailOnPress}
                disabled={Boolean(disableResendEmail)}
                className={classes.resendEmailButton}
              >
                {t("signUpFlow.verifyEmail.resend")}
              </button>

              {disableResendEmail && timeLeft && (
                <div className={classes.timerContainer}>
                  <div className={classes.spinner}>
                    <div></div>
                    <div></div>
                    <div></div>
                    <div></div>
                  </div>
                  <p className={classes.timerText}>
                    {t("signUpFlow.verifyEmail.timer", {
                      number: Math.ceil(timeLeft / 1000),
                    })}
                  </p>
                </div>
              )}
            </div>
          </div>
        </>
      )}
      <Footer />
    </div>
  );
};

export { AddEmail };
